import React from 'react';
import { Box, Typography, Card, CardContent, CardMedia, Grid, Chip, Tooltip } from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import CollectionActionButtons from '../../../../components/common/CollectionActionButtons';

const EmptyState = ({ message }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <AutoStoriesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
    <Typography variant="h6" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const ContentDisplay = ({ contents, viewType, tabValue, tabConstants, onActionComplete }) => {
  const navigate = useNavigate();
  const isRequest = tabValue === tabConstants?.requestsTab;

  if (!contents || contents.length === 0) {
    return <EmptyState message={isRequest ? "No in-progress requests found." : "No collections found."} />;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'FAILED':
        return 'error';
      case 'MANUAL_REVIEW':
        return 'warning';
      case 'GENERATING_OUTLINE':
      case 'GENERATING_PRACTICES':
      case 'CREATING_COLLECTION':
        return 'info';
      case 'COMPLETED':
      case 'PUBLISHED':
        return 'success';
      default:
        return 'primary';
    }
  };

  const getTitle = (item) => {
    if (isRequest) {
      return item.sourceMaterial
        ? (item.sourceMaterial.length > 80 ? item.sourceMaterial.substring(0, 80) + '...' : item.sourceMaterial)
        : 'Untitled Request';
    }
    return item.title;
  };

  const getDescription = (item) => {
    if (isRequest) {
      const typeStr = item.type ? (item.type.charAt(0) + item.type.slice(1).toLowerCase()).replace('_', ' ') : 'N/A';
      return `Type: ${typeStr} | Practices to generate: ${item.numberOfPractices || 0}`;
    }
    return item.description || item.synopsis || "No description available";
  };

  const handleCardClick = (item) => {
    if (isRequest) {
      navigate(`/practice-collections/requests/${item.id}`);
    } else {
      navigate(`/practice-collections/${item.id}`);
    }
  };

  return (
    <Grid container spacing={3}>
      {contents.map((item, index) => (
        <Grid 
          item 
          xs={12} 
          sm={viewType === 'grid' ? 6 : 12} 
          md={viewType === 'grid' ? 4 : 12} 
          key={item.id}
          className="card-fade-in"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <Card 
            sx={{ 
              display: 'flex', 
              flexDirection: viewType === 'list' ? 'row' : 'column', 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              borderColor: 'transparent',
              borderWidth: 1,
              borderStyle: 'solid',
              '&:hover': {
                transform: 'translateY(-5px) scale(1.01)',
                borderColor: (theme) => theme.palette.primary.main,
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0px 12px 30px rgba(181, 235, 185, 0.2)'
                  : '0px 12px 30px rgba(0, 65, 73, 0.08)',
              }
            }}
            onClick={() => handleCardClick(item)}
          >
            {!isRequest && item.coverImageUrl && (
              <CardMedia
                component="img"
                sx={{ 
                  width: viewType === 'list' ? 200 : '100%', 
                  height: viewType === 'list' ? 'auto' : 140 
                }}
                image={item.coverImageUrl}
                alt={item.title}
              />
            )}
            <CardContent sx={{ flex: 1 }}>
              <Typography gutterBottom variant="h6" component="div">
                {getTitle(item)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getDescription(item)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip label={item.status} size="small" color={getStatusColor(item.status)} />
                  {item.schoolLevel && <Chip label={item.schoolLevel} size="small" variant="outlined" />}
                  <Tooltip 
                    title={
                      item.type?.toUpperCase() === 'PATHWAY' 
                        ? (item.pathways && item.pathways.length > 0 
                            ? `Attached to: ${item.pathways.map(p => p.name).join(', ')}` 
                            : "Not attached to any practice pathway") 
                        : ""
                    } 
                    placement="top"
                    disableHoverListener={item.type?.toUpperCase() !== 'PATHWAY'}
                  >
                    <Chip
                      icon={item.type?.toUpperCase() === 'PATHWAY' ? <TimelineIcon fontSize="small" /> : <AutoStoriesIcon fontSize="small" />}
                      label={item.type?.toUpperCase() === 'PATHWAY' ? 'Pathway' : 'Paired Reading'}
                      size="small"
                      variant="outlined"
                      color={item.type?.toUpperCase() === 'PATHWAY' ? 'primary' : 'secondary'}
                      sx={{
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                          fontSize: '0.9rem',
                        }
                      }}
                    />
                  </Tooltip>
                  {item.type?.toUpperCase() === 'PATHWAY' && (!item.pathways || item.pathways.length === 0) && (
                    <Tooltip title="This collection is not attached to any practice pathway. Edit a pathway to add it." placement="top">
                      <Chip
                        label="Orphan"
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ 
                          fontWeight: 'bold', 
                          borderWidth: 1.5,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {!isRequest && (
                  <CollectionActionButtons
                    collectionId={item.id}
                    status={item.status}
                    onActionComplete={onActionComplete}
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default ContentDisplay;
